import { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import wasteTrackingService from '../services/wasteTrackingService';

// Normalise inventory items regardless of API response shape — the real
// /api/inventory/items payload doesn't necessarily use flat field names
// (uomSymbol, currentStock, unitCost). This mirrors the same defensive
// mapping PurchaseOrderForm does for its own inventory item list, so the
// waste-log item dropdown doesn't end up empty just because a field is
// nested (e.g. item.uom.symbol) or named differently (e.g. item.costPrice).
const normaliseInventoryItems = (raw) => {
  const arr = Array.isArray(raw) ? raw
    : Array.isArray(raw?.data) ? raw.data
    : Array.isArray(raw?.content) ? raw.content
    : [];
  return arr.map((item) => ({
    id: item.id,
    name: item.name ?? item.itemName ?? item.title ?? '',
    uomSymbol: item.uomSymbol
      ?? item.uom?.symbol
      ?? item.unitOfMeasurement?.symbol
      ?? item.idUom?.symbol
      ?? (typeof item.uom === 'string' ? item.uom : null)
      ?? 'unit',
    unitCost: item.unitCost ?? item.costPrice ?? item.cost ?? 0,
    currentStock: item.currentStock
      ?? item.stock
      ?? item.availableQuantity
      ?? item.quantityOnHand
      ?? item.quantity
      ?? 0,
  }));
};

export const useWasteTracking = (selectedBranchId, analyticsPeriod = 7) => {
  // State for all data
  const [todaysWaste, setTodaysWaste] = useState([]);
  const [stats, setStats] = useState({ totalWasteCount: 0, wasteCost: 0, topCategory: 'N/A', wasteVsRevenuePercent: 0 });
  const [analytics, setAnalytics] = useState(null);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [wasteEntries, setWasteEntries] = useState([]);
  
  // Common states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch all data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Get the effective branch ID (same logic as original component)
      const branchId = selectedBranchId || 1; // Default to 1 if no branch selected
      
      const [itemsRes, statsRes, todayRes, entriesRes, analyticsRes] = await Promise.allSettled([
        wasteTrackingService.getInventoryItems(branchId),
        wasteTrackingService.getStats(branchId),
        wasteTrackingService.getTodaysWaste(branchId),
        wasteTrackingService.getWasteEntries({ branchId }),
        wasteTrackingService.getAnalytics(branchId, analyticsPeriod),
      ]);

      // Handle each response
      if (itemsRes.status === 'fulfilled') {
        const normalised = normaliseInventoryItems(itemsRes.value);
        setInventoryItems(normalised);
        if (normalised.length === 0) {
          // Fulfilled but empty — likely an unexpected response shape rather
          // than "no items exist". Log the raw payload so it's easy to spot
          // which field names actually came back from the API.
          console.warn('[useWasteTracking] getInventoryItems returned 0 items after normalising. Raw response:', itemsRes.value);
        }
      } else {
        // Previously this branch did nothing, so a failed items fetch left
        // inventoryItems as [] with no visible error — the Log Waste modal's
        // dropdown just looked permanently empty. Surface it instead.
        console.error('[useWasteTracking] Failed to load inventory items:', itemsRes.reason);
        toast.error('Failed to load inventory items — the item list may be incomplete');
      }

      if (statsRes.status === 'fulfilled') {
        const s = statsRes.value?.data || statsRes.value || {};
        setStats({ ...s });
      }

      if (todayRes.status === 'fulfilled') {
        const todayData = Array.isArray(todayRes.value) ? todayRes.value : todayRes.value?.data || [];
        setTodaysWaste(todayData);
      }

      if (entriesRes.status === 'fulfilled') {
        setWasteEntries(Array.isArray(entriesRes.value) ? entriesRes.value : entriesRes.value?.data || []);
      }

      if (analyticsRes.status === 'fulfilled') {
        setAnalytics(analyticsRes.value);
      }

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedBranchId, analyticsPeriod]);

  // Log waste function
  const logWaste = useCallback(async (payload) => {
    try {
      const response = await wasteTrackingService.logWaste(payload);
      // Refresh data after logging
      await fetchData();
      return response;
    } catch (err) {
      throw err;
    }
  }, [fetchData]);

  // Initial load and when dependencies change
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    // Data
    todaysWaste,
    stats,
    analytics,
    inventoryItems,
    wasteEntries,
    
    // States
    loading,
    error,
    
    // Actions
    refetch: fetchData,
    logWaste,
  };
};